/**
 * Which white-glove buttons a step offers. Mirrors the API's runner rules (api/src/domain/runner.ts) so the page
 * only offers what can apply; the API still decides, and a 409 carries its message.
 *
 * - Run: the run isn't executing, the step isn't running, and every step before it is settled.
 * - Skip / Done by hand: the step isn't running. Hidden as a no-op when the step is already skipped / done.
 */
import type { RunStatus, Step, StepStatus } from './schemas'

export interface ActionRule {
  enabled: boolean
  label: string
  /** Why it's disabled, for the button's title. */
  reason?: string
}

export interface StepActionSet {
  run: ActionRule
  skip: ActionRule
  done: ActionRule
}

const UNSETTLED: ReadonlySet<StepStatus> = new Set(['pending', 'failed'])

function runLabel(status: StepStatus): string {
  if (status === 'failed') return 'Retry'
  if (status === 'done' || status === 'skipped' || status === 'manual') return 'Run again'
  return 'Run'
}

export function stepActions(
  run: { status: RunStatus; steps: Pick<Step, 'key' | 'status'>[] },
  key: string,
  opts: { active?: boolean } = {},
): StepActionSet {
  const idx = run.steps.findIndex((s) => s.key === key)
  const step = run.steps[idx]
  if (!step) {
    const off = { enabled: false, reason: `no step ${key}` }
    return { run: { ...off, label: 'Run' }, skip: { ...off, label: 'Skip' }, done: { ...off, label: 'Done by hand' } }
  }
  const executing = opts.active ?? run.status === 'running'
  const isRunning = step.status === 'running'
  const blocker = run.steps.slice(0, idx).find((s) => UNSETTLED.has(s.status))

  const runRule: ActionRule = { label: runLabel(step.status), enabled: true }
  if (isRunning) Object.assign(runRule, { enabled: false, reason: 'running now' })
  else if (executing) Object.assign(runRule, { enabled: false, reason: 'the run is executing' })
  else if (blocker) Object.assign(runRule, { enabled: false, reason: `${blocker.key} comes first and isn't settled` })

  const skip: ActionRule = { label: 'Skip', enabled: true }
  if (isRunning) Object.assign(skip, { enabled: false, reason: 'running now' })
  else if (step.status === 'skipped') Object.assign(skip, { enabled: false, reason: 'already skipped' })

  const done: ActionRule = { label: 'Done by hand', enabled: true }
  if (isRunning) Object.assign(done, { enabled: false, reason: 'running now' })
  else if (step.status === 'done') Object.assign(done, { enabled: false, reason: 'already done' })

  return { run: runRule, skip, done }
}

/** Teardown needs the founder's slug typed exactly. */
export function teardownConfirmed(typed: string, slug: string): boolean {
  return typed.trim() === slug && slug.length > 0
}
